/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { INITIALIZER_PROGRAM_ADDRESS } from '../programs';

export const FUND_VESTING_DISCRIMINATOR = new Uint8Array([
  57, 58, 127, 28, 175, 154, 17, 125,
]);

export function getFundVestingDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    FUND_VESTING_DISCRIMINATOR,
  );
}

export type FundVestingInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountLaunch extends string | AccountMeta<string> = string,
  TAccountLaunchAuthority extends string | AccountMeta<string> = string,
  TAccountVestingConfig extends string | AccountMeta<string> = string,
  TAccountBaseMint extends string | AccountMeta<string> = string,
  TAccountBaseVault extends string | AccountMeta<string> = string,
  TAccountVestingVault extends string | AccountMeta<string> = string,
  TAccountPayer extends string | AccountMeta<string> = string,
  TAccountBaseTokenProgram extends string | AccountMeta<string> = string,
  TAccountAssociatedTokenProgram extends string | AccountMeta<string> =
    'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL',
  TAccountSystemProgram extends string | AccountMeta<string> =
    '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountLaunch extends string
        ? WritableAccount<TAccountLaunch>
        : TAccountLaunch,
      TAccountLaunchAuthority extends string
        ? ReadonlyAccount<TAccountLaunchAuthority>
        : TAccountLaunchAuthority,
      TAccountVestingConfig extends string
        ? ReadonlyAccount<TAccountVestingConfig>
        : TAccountVestingConfig,
      TAccountBaseMint extends string
        ? ReadonlyAccount<TAccountBaseMint>
        : TAccountBaseMint,
      TAccountBaseVault extends string
        ? WritableAccount<TAccountBaseVault>
        : TAccountBaseVault,
      TAccountVestingVault extends string
        ? WritableAccount<TAccountVestingVault>
        : TAccountVestingVault,
      TAccountPayer extends string
        ? WritableSignerAccount<TAccountPayer> &
            AccountSignerMeta<TAccountPayer>
        : TAccountPayer,
      TAccountBaseTokenProgram extends string
        ? ReadonlyAccount<TAccountBaseTokenProgram>
        : TAccountBaseTokenProgram,
      TAccountAssociatedTokenProgram extends string
        ? ReadonlyAccount<TAccountAssociatedTokenProgram>
        : TAccountAssociatedTokenProgram,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type FundVestingInstructionData = { discriminator: ReadonlyUint8Array };

export type FundVestingInstructionDataArgs = {};

export function getFundVestingInstructionDataEncoder(): FixedSizeEncoder<FundVestingInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({ ...value, discriminator: FUND_VESTING_DISCRIMINATOR }),
  );
}

export function getFundVestingInstructionDataDecoder(): FixedSizeDecoder<FundVestingInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getFundVestingInstructionDataCodec(): FixedSizeCodec<
  FundVestingInstructionDataArgs,
  FundVestingInstructionData
> {
  return combineCodec(
    getFundVestingInstructionDataEncoder(),
    getFundVestingInstructionDataDecoder(),
  );
}

export type FundVestingAsyncInput<
  TAccountLaunch extends string = string,
  TAccountLaunchAuthority extends string = string,
  TAccountVestingConfig extends string = string,
  TAccountBaseMint extends string = string,
  TAccountBaseVault extends string = string,
  TAccountVestingVault extends string = string,
  TAccountPayer extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountAssociatedTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  launch: Address<TAccountLaunch>;
  launchAuthority: Address<TAccountLaunchAuthority>;
  vestingConfig?: Address<TAccountVestingConfig>;
  baseMint: Address<TAccountBaseMint>;
  baseVault: Address<TAccountBaseVault>;
  vestingVault?: Address<TAccountVestingVault>;
  payer: TransactionSigner<TAccountPayer>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  associatedTokenProgram?: Address<TAccountAssociatedTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
};

export async function getFundVestingInstructionAsync<
  TAccountLaunch extends string,
  TAccountLaunchAuthority extends string,
  TAccountVestingConfig extends string,
  TAccountBaseMint extends string,
  TAccountBaseVault extends string,
  TAccountVestingVault extends string,
  TAccountPayer extends string,
  TAccountBaseTokenProgram extends string,
  TAccountAssociatedTokenProgram extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: FundVestingAsyncInput<
    TAccountLaunch,
    TAccountLaunchAuthority,
    TAccountVestingConfig,
    TAccountBaseMint,
    TAccountBaseVault,
    TAccountVestingVault,
    TAccountPayer,
    TAccountBaseTokenProgram,
    TAccountAssociatedTokenProgram,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  FundVestingInstruction<
    TProgramAddress,
    TAccountLaunch,
    TAccountLaunchAuthority,
    TAccountVestingConfig,
    TAccountBaseMint,
    TAccountBaseVault,
    TAccountVestingVault,
    TAccountPayer,
    TAccountBaseTokenProgram,
    TAccountAssociatedTokenProgram,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    launch: { value: input.launch ?? null, isWritable: true },
    launchAuthority: {
      value: input.launchAuthority ?? null,
      isWritable: false,
    },
    vestingConfig: { value: input.vestingConfig ?? null, isWritable: false },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    baseVault: { value: input.baseVault ?? null, isWritable: true },
    vestingVault: { value: input.vestingVault ?? null, isWritable: true },
    payer: { value: input.payer ?? null, isWritable: true },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    associatedTokenProgram: {
      value: input.associatedTokenProgram ?? null,
      isWritable: false,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Resolve default values.
  if (!accounts.vestingConfig.value) {
    accounts.vestingConfig.value = await getProgramDerivedAddress({
      programAddress:
        '2dhWRsojcuJbEJ2uaB6ayGwXz6PcFaPGpjnqP9Bawead' as Address<'2dhWRsojcuJbEJ2uaB6ayGwXz6PcFaPGpjnqP9Bawead'>,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            118, 101, 115, 116, 105, 110, 103, 95, 99, 111, 110, 102, 105, 103,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'launch',
            accounts.launch.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseMint',
            accounts.baseMint.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.vestingVault.value) {
    accounts.vestingVault.value = await getProgramDerivedAddress({
      programAddress:
        'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL' as Address<'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'>,
      seeds: [
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'vestingConfig',
            accounts.vestingConfig.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseTokenProgram',
            accounts.baseTokenProgram.value,
          ),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseMint',
            accounts.baseMint.value,
          ),
        ),
      ],
    });
  }
  if (!accounts.associatedTokenProgram.value) {
    accounts.associatedTokenProgram.value =
      'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL' as Address<'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'>;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('launchAuthority', accounts.launchAuthority),
      getAccountMeta('vestingConfig', accounts.vestingConfig),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('baseVault', accounts.baseVault),
      getAccountMeta('vestingVault', accounts.vestingVault),
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('associatedTokenProgram', accounts.associatedTokenProgram),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getFundVestingInstructionDataEncoder().encode({}),
    programAddress,
  } as FundVestingInstruction<
    TProgramAddress,
    TAccountLaunch,
    TAccountLaunchAuthority,
    TAccountVestingConfig,
    TAccountBaseMint,
    TAccountBaseVault,
    TAccountVestingVault,
    TAccountPayer,
    TAccountBaseTokenProgram,
    TAccountAssociatedTokenProgram,
    TAccountSystemProgram
  >);
}

export type FundVestingInput<
  TAccountLaunch extends string = string,
  TAccountLaunchAuthority extends string = string,
  TAccountVestingConfig extends string = string,
  TAccountBaseMint extends string = string,
  TAccountBaseVault extends string = string,
  TAccountVestingVault extends string = string,
  TAccountPayer extends string = string,
  TAccountBaseTokenProgram extends string = string,
  TAccountAssociatedTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  launch: Address<TAccountLaunch>;
  launchAuthority: Address<TAccountLaunchAuthority>;
  vestingConfig: Address<TAccountVestingConfig>;
  baseMint: Address<TAccountBaseMint>;
  baseVault: Address<TAccountBaseVault>;
  vestingVault: Address<TAccountVestingVault>;
  payer: TransactionSigner<TAccountPayer>;
  baseTokenProgram: Address<TAccountBaseTokenProgram>;
  associatedTokenProgram?: Address<TAccountAssociatedTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
};

export function getFundVestingInstruction<
  TAccountLaunch extends string,
  TAccountLaunchAuthority extends string,
  TAccountVestingConfig extends string,
  TAccountBaseMint extends string,
  TAccountBaseVault extends string,
  TAccountVestingVault extends string,
  TAccountPayer extends string,
  TAccountBaseTokenProgram extends string,
  TAccountAssociatedTokenProgram extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof INITIALIZER_PROGRAM_ADDRESS,
>(
  input: FundVestingInput<
    TAccountLaunch,
    TAccountLaunchAuthority,
    TAccountVestingConfig,
    TAccountBaseMint,
    TAccountBaseVault,
    TAccountVestingVault,
    TAccountPayer,
    TAccountBaseTokenProgram,
    TAccountAssociatedTokenProgram,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): FundVestingInstruction<
  TProgramAddress,
  TAccountLaunch,
  TAccountLaunchAuthority,
  TAccountVestingConfig,
  TAccountBaseMint,
  TAccountBaseVault,
  TAccountVestingVault,
  TAccountPayer,
  TAccountBaseTokenProgram,
  TAccountAssociatedTokenProgram,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? INITIALIZER_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    launch: { value: input.launch ?? null, isWritable: true },
    launchAuthority: {
      value: input.launchAuthority ?? null,
      isWritable: false,
    },
    vestingConfig: { value: input.vestingConfig ?? null, isWritable: false },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    baseVault: { value: input.baseVault ?? null, isWritable: true },
    vestingVault: { value: input.vestingVault ?? null, isWritable: true },
    payer: { value: input.payer ?? null, isWritable: true },
    baseTokenProgram: {
      value: input.baseTokenProgram ?? null,
      isWritable: false,
    },
    associatedTokenProgram: {
      value: input.associatedTokenProgram ?? null,
      isWritable: false,
    },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Resolve default values.
  if (!accounts.associatedTokenProgram.value) {
    accounts.associatedTokenProgram.value =
      'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL' as Address<'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL'>;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('launch', accounts.launch),
      getAccountMeta('launchAuthority', accounts.launchAuthority),
      getAccountMeta('vestingConfig', accounts.vestingConfig),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('baseVault', accounts.baseVault),
      getAccountMeta('vestingVault', accounts.vestingVault),
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('baseTokenProgram', accounts.baseTokenProgram),
      getAccountMeta('associatedTokenProgram', accounts.associatedTokenProgram),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getFundVestingInstructionDataEncoder().encode({}),
    programAddress,
  } as FundVestingInstruction<
    TProgramAddress,
    TAccountLaunch,
    TAccountLaunchAuthority,
    TAccountVestingConfig,
    TAccountBaseMint,
    TAccountBaseVault,
    TAccountVestingVault,
    TAccountPayer,
    TAccountBaseTokenProgram,
    TAccountAssociatedTokenProgram,
    TAccountSystemProgram
  >);
}

export type ParsedFundVestingInstruction<
  TProgram extends string = typeof INITIALIZER_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    launch: TAccountMetas[0];
    launchAuthority: TAccountMetas[1];
    vestingConfig: TAccountMetas[2];
    baseMint: TAccountMetas[3];
    baseVault: TAccountMetas[4];
    vestingVault: TAccountMetas[5];
    payer: TAccountMetas[6];
    baseTokenProgram: TAccountMetas[7];
    associatedTokenProgram: TAccountMetas[8];
    systemProgram: TAccountMetas[9];
  };
  data: FundVestingInstructionData;
};

export function parseFundVestingInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedFundVestingInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 10) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 10,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      launch: getNextAccount(),
      launchAuthority: getNextAccount(),
      vestingConfig: getNextAccount(),
      baseMint: getNextAccount(),
      baseVault: getNextAccount(),
      vestingVault: getNextAccount(),
      payer: getNextAccount(),
      baseTokenProgram: getNextAccount(),
      associatedTokenProgram: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getFundVestingInstructionDataDecoder().decode(instruction.data),
  };
}
